// Phase SA-4/4 — Brief §03 A2: Market performance matrix.
//
// 4-col grid showing where the user wins and where they lose:
//   Market | Legs | Win rate · Process accuracy | Your rate vs STRIKZA
//
// Reads from user_betting_profile.market_performance (JSONB):
//   { '<market_slug>': { legs, wins, rate, process_accuracy, strikza_rate? } }
//
// Rules (brief verbatim):
//   • 8 fixed markets listed in MARKETS below.
//   • Min 3 legs before showing rate → otherwise "Insufficient data".
//   • Cell tone: > 60% green / 40-60% amber / < 40% red.
//   • vs-STRIKZA column: Pro-only. Free users see the column with
//     a 🔒 chip in the head + the cells blurred.
//   • Mobile (< 720px): CSS drops the vs-STRIKZA column entirely
//     (.sa-mm-pro-col is handled by SA-3 @media).
//   • Empty state: all 8 rows render with '—' in every cell.

import type { MarketPerformanceEntry } from "../../slips/useBettingProfile.ts";

/** The 8 markets the brief pins. Order matters — same order on
 * every render so the table is scan-able. */
export const MARKETS: ReadonlyArray<{ slug: string; label: string }> = [
  { slug: "match_winner", label: "Match Winner" },
  { slug: "asian_handicap", label: "Asian Handicap" },
  { slug: "btts", label: "BTTS" },
  { slug: "over_under_goals", label: "Over/Under Goals" },
  { slug: "correct_score", label: "Correct Score" },
  { slug: "cards", label: "Cards" },
  { slug: "corners", label: "Corners" },
  { slug: "player_goals", label: "Player Goals" },
];

/** Pure derivation: cell tone from win-rate decimal. */
export function toneForRate(rate: number | null): "good" | "warn" | "bad" | undefined {
  if (rate == null || !Number.isFinite(rate)) return undefined;
  if (rate > 0.6) return "good";
  if (rate >= 0.4) return "warn";
  return "bad";
}

function pct(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return `${Math.round(n * 100)}%`;
}

export interface MarketPerformanceMatrixProps {
  /** The JSONB blob from user_betting_profile.market_performance.
   * Missing keys are treated as empty (Insufficient data). */
  data: Record<string, MarketPerformanceEntry>;
  /** Pro flag controls the vs-STRIKZA column visibility / blur. */
  pro?: boolean;
}

export function MarketPerformanceMatrix({
  data,
  pro = false,
}: MarketPerformanceMatrixProps): JSX.Element {
  return (
    <div
      data-testid="sa-market-matrix"
      data-pro={pro || undefined}
      className="sa-market-matrix"
      role="table"
      aria-label="Market performance matrix"
    >
      {/* Header row */}
      <div className="sa-mm-head" role="columnheader">
        Market
      </div>
      <div className="sa-mm-head" role="columnheader">
        Legs
      </div>
      <div className="sa-mm-head" role="columnheader">
        Win · Process
      </div>
      <div className="sa-mm-head sa-mm-pro-col" role="columnheader">
        Your rate vs STRIKZA
        {!pro ? (
          <span
            data-testid="sa-mm-pro-lock"
            style={{
              marginLeft: 6,
              padding: "1px 6px",
              border: "1px solid var(--sa-hairline-accent)",
              borderRadius: 999,
              color: "var(--sa-accent-saffron)",
              fontSize: 9,
              letterSpacing: "0.18em",
            }}
            title="Unlock with SIKA Pro"
          >
            🔒 PRO
          </span>
        ) : null}
      </div>

      {/* Rows — one per fixed market slug */}
      {MARKETS.map(({ slug, label }) => {
        const row = data[slug];
        const insufficient = !row || row.legs < 3;

        return (
          <MarketRow
            key={slug}
            slug={slug}
            label={label}
            row={row}
            insufficient={insufficient}
            pro={pro}
          />
        );
      })}
    </div>
  );
}

function MarketRow({
  slug,
  label,
  row,
  insufficient,
  pro,
}: {
  slug: string;
  label: string;
  row: MarketPerformanceEntry | undefined;
  insufficient: boolean;
  pro: boolean;
}): JSX.Element {
  const legs = row?.legs ?? 0;
  const rate = row?.rate ?? null;
  const process = row?.process_accuracy ?? null;
  const strikzaRate = row?.strikza_rate ?? null;

  return (
    <>
      <div
        className="sa-mm-cell"
        data-testid={`sa-mm-${slug}-label`}
        role="cell"
        style={{ fontWeight: 500 }}
      >
        {label}
      </div>

      <div
        className="sa-mm-cell"
        data-testid={`sa-mm-${slug}-legs`}
        role="cell"
        data-empty={legs === 0 ? "true" : undefined}
      >
        {legs === 0 ? "—" : legs}
      </div>

      {insufficient ? (
        <div
          className="sa-mm-cell"
          data-testid={`sa-mm-${slug}-rate`}
          role="cell"
          data-empty="true"
        >
          {legs === 0 ? "—" : "Insufficient data"}
        </div>
      ) : (
        <div
          className="sa-mm-cell"
          data-testid={`sa-mm-${slug}-rate`}
          role="cell"
          data-tone={toneForRate(rate)}
        >
          <span style={{ fontFamily: "var(--sa-font-stat)", fontSize: 17 }}>
            {pct(rate)}
          </span>
          <span style={{ color: "var(--sa-fg-muted)", marginLeft: 6 }}>
            · {pct(process)}
          </span>
        </div>
      )}

      <div
        className="sa-mm-cell sa-mm-pro-col"
        data-testid={`sa-mm-${slug}-vs`}
        role="cell"
        data-empty={insufficient || strikzaRate == null ? "true" : undefined}
        style={!pro ? { filter: "blur(4px)", userSelect: "none" } : undefined}
        aria-hidden={!pro ? "true" : undefined}
      >
        {insufficient || strikzaRate == null
          ? "—"
          : rate == null
            ? "—"
            : renderVsModel(rate, strikzaRate)}
      </div>
    </>
  );
}

function renderVsModel(userRate: number, modelRate: number): JSX.Element {
  const diff = (userRate - modelRate) * 100;
  const sign = diff >= 0 ? "+" : "";
  const color =
    diff > 0
      ? "var(--sa-signal-green)"
      : diff < 0
        ? "var(--sa-signal-red)"
        : "var(--sa-fg-muted)";
  // Template literals to avoid React's <!-- --> markers between
  // adjacent text + expression nodes (would break `.toContain`
  // tests + visually splits the output).
  return (
    <span style={{ color }}>
      {`${sign}${diff.toFixed(0)} `}
      <span style={{ color: "var(--sa-fg-muted)", fontSize: 11 }}>
        {`vs ${Math.round(modelRate * 100)}%`}
      </span>
    </span>
  );
}
